import { useTranslation } from "react-i18next";

import { css } from "@emotion/react";

import { colors } from "@dotnet/telematics-design-system";

import Autocomplete from "components/DesignComponents/Autocomplete";
import Checkbox from "components/DesignComponents/Checkbox";
import {
    TOOLTIP_PLACEMENT,
    TooltipLarge
} from "components/DesignComponents/Tooltips";
import HighlightedOption from "components/HighlightedOption";
import { DROPDOWN_SIZES } from "shared";

import { Task } from "../types";
import CompatibilityContent from "./CompatibilityContent";
import { renderPlatforms } from "./functions";
import {
    useBluetoothPassword,
    useCanAdapters,
    useCanVehicles,
    useCreateTaskContent,
    useFeatureControl,
    useGeneralDropdown,
    useInternalLog
} from "./hooks";
import ScheduleTask, { useScheduleTask } from "./ScheduleTask";
import SecondSelectContent from "./SecondSelectContent";
import { CreateTaskContentProps, SCHEDULE_TASK_TYPE } from "./types";

const CreateTaskContent = ({
    isLoading,
    isFinalStep,
    activeSource,
    compatibilityData,
    hasCreateForIncompatible,
    setPayload,
    handleCreateForIncompatible
}: CreateTaskContentProps) => {
    const { textPlaceholder } = colors;

    const { t } = useTranslation();

    const {
        arePlatformsLoading,
        isCancelTasksChecked,
        isRestricted,
        scheduleType,
        taskTypeInputValue,
        selectedTask,
        tasks,
        setTaskTypeInputValue,
        handleChangeValue,
        handleScheduledDate,
        handleScheduledTime,
        handleSecondSelectData,
        handleScheduleType,
        toggleTaskCancellation
    } = useCreateTaskContent(setPayload);

    const bluetoothPasswordProps = useBluetoothPassword(selectedTask);
    const canAdapterProps = useCanAdapters(selectedTask);
    const canVehiclesProps = useCanVehicles(selectedTask);
    const featureControlProps = useFeatureControl(selectedTask);
    const generalDropdownProps = useGeneralDropdown(selectedTask);
    const internalLogProps = useInternalLog(selectedTask);

    const { scheduleDate, scheduleTime, scheduleForCompatibility } =
        useScheduleTask(scheduleType, handleScheduledDate, handleScheduledTime);

    return isFinalStep && compatibilityData ? (
        <CompatibilityContent
            data={compatibilityData}
            activeSource={activeSource}
            secondSelectName={selectedTask?.secondSelect}
            differentData={{
                internalLogProps,
                bluetoothPasswordProps,
                canAdapterProps,
                canVehiclesProps,
                featureControlProps,
                generalDropdownProps
            }}
            isCancelTasksChecked={isCancelTasksChecked}
            schedule={scheduleForCompatibility}
            hasCreateForIncompatible={hasCreateForIncompatible}
            isLoading={isLoading}
            handleCreateForIncompatible={handleCreateForIncompatible}
        />
    ) : (
        <>
            <Autocomplete
                data-testid="task-type-dropdown"
                fullWidth
                isRequired
                size={DROPDOWN_SIZES.Medium}
                labelLeft={t("General##task type")}
                textFieldParams={{
                    placeholder: t("Dialog##select")
                }}
                options={tasks}
                groupBy={option => t(`Dialog##${option.group}`)}
                getOptionLabel={option => t(`General##${option.type}`)}
                renderOption={(props, option: Task) => {
                    const { key, ...restProps } = props;

                    const platforms = option.platforms;

                    const shouldRenderPlatforms =
                        platforms && platforms.length > 0;

                    return (
                        <li
                            key={key}
                            {...restProps}
                            css={css({
                                flexWrap: "wrap",
                                columnGap: "5px"
                            })}
                        >
                            <span>
                                <HighlightedOption
                                    text={t(`General##${option.type}`)}
                                    query={taskTypeInputValue}
                                />
                            </span>

                            {shouldRenderPlatforms && (
                                <span
                                    css={css({
                                        color: textPlaceholder
                                    })}
                                >
                                    ({renderPlatforms(platforms, t)})
                                </span>
                            )}
                        </li>
                    );
                }}
                onClose={() => setTaskTypeInputValue("")}
                isOptionEqualToValue={(option, value) =>
                    option.type === value.type
                }
                value={selectedTask}
                loading={arePlatformsLoading}
                readOnly={isLoading}
                onInputChange={(_, newInputValue) =>
                    setTaskTypeInputValue(newInputValue)
                }
                onChange={(_, taskType) => handleChangeValue(taskType)}
                customStyle={{
                    marginTop: "16px"
                }}
            />

            {selectedTask?.secondSelect && (
                <SecondSelectContent
                    secondSelectName={selectedTask.secondSelect}
                    type={selectedTask.type}
                    isLoading={isLoading}
                    bluetoothPasswordProps={bluetoothPasswordProps}
                    canAdapterProps={canAdapterProps}
                    canVehiclesProps={canVehiclesProps}
                    featureControlProps={featureControlProps}
                    generalDropdownProps={generalDropdownProps}
                    internalLogProps={internalLogProps}
                    changePayload={handleSecondSelectData}
                />
            )}

            <div
                css={css({
                    margin: "16px 0"
                })}
            >
                <Checkbox
                    data-testid="cancel-pending-tasks-checkbox"
                    label={t("Dialog##cancel pending tasks")}
                    onChange={toggleTaskCancellation}
                    checked={isCancelTasksChecked}
                    disabled={isLoading}
                />
            </div>

            <TooltipLarge
                placement={TOOLTIP_PLACEMENT.Top}
                title={isRestricted ? t(`Restrictions##general message`) : ""}
            >
                <span
                    css={css({
                        display: "block",
                        marginBottom: "16px"
                    })}
                >
                    <Autocomplete
                        fullWidth
                        size={DROPDOWN_SIZES.Medium}
                        labelLeft={t("Dialog##schedule task")}
                        textFieldParams={{
                            placeholder: t("Dialog##select")
                        }}
                        options={Object.values(SCHEDULE_TASK_TYPE)}
                        getOptionLabel={option => t(`Dialog##${option}`)}
                        value={scheduleType}
                        readOnly={isRestricted || isLoading}
                        onChange={(_, type) => handleScheduleType(type)}
                    />
                </span>
            </TooltipLarge>

            {scheduleType && (
                <ScheduleTask
                    type={scheduleType}
                    isLoading={isLoading}
                    scheduleDate={scheduleDate}
                    scheduleTime={scheduleTime}
                />
            )}
        </>
    );
};

export default CreateTaskContent;
